import { VisualizatorParser } from "../core/visualizator-parser";
import { VisualizatorSettingsParser } from "../core/settings-parsers/visualizator-settings-parser";
import { ModeSettingsParser } from "../core/settings-parsers/mode-settings-parser";
import { UnitSettingsParser } from "../core/settings-parsers/unit-settings-parser";
import { FilteredDataSettingsParser } from "../core/settings-parsers/filtered-data-settings-parser";
import { SimpleSettingsParser } from "../core/settings-parsers/simple-settings-parser";
import { CommonDataSourceParser } from "../core/settings-parsers/common-data-source-parser";
import { CommonChannelParser } from "../core/settings-parsers/common-channel-parser";
import { CommonMarkParser } from "../core/settings-parsers/common-mark-parser";
import { CommonChannelConfigurationParser } from "../core/settings-parsers/common-channel-configuration-parser";
import { CommonSignalParser } from "../core/settings-parsers/common-signal-parser";
import { ViewSettingsParser } from "../core/settings-parsers/view-settings-parser";
import { FpsCanvasParser } from "../core/settings-parsers/fps-canvas-parser";
import { PanelSettingsParser } from "../core/settings-parsers/view-objects/panel-settings-parser";
import { GridSettingsParser } from "../core/settings-parsers/view-objects/grid-settings-parser";
import { ColumnGridSettingsParser } from "../core/settings-parsers/view-objects/column-grid-settings-parser";
import { TableSettingsParser } from "../core/settings-parsers/view-objects/table-settings-parser";
import { PlotSettingsParser } from "../core/settings-parsers/view-objects/plot-settings-parser";
import { DataSourcePlotSettingsParser } from "../core/settings-parsers/view-objects/plot/data-source-plot-settings-parser";
import { MarkPlotSettingsParser } from "../core/settings-parsers/view-objects/plot/mark-plot-settings-parser";
import { ChannelPlotSettingsParser } from "../core/settings-parsers/view-objects/plot/channel-plot-settings-parser";
import { ChannelConfigurationPlotSettingsParser } from "../core/settings-parsers/view-objects/plot/channel-configuration-plot-settings-parser";
import { IsolinePlotSettingsParser } from "../core/settings-parsers/view-objects/plot/isoline-plot-settings-parser";
import { SignalPlotSettingsParser } from "../core/settings-parsers/view-objects/plot/signal-plot-settings-parser";
import { FilterParser } from "../core/settings-parsers/filter-parser";

import { SettingsArrayParser } from "../core/settings-parsers/settings-array-parser";
import { PlotContentParser } from "../core/settings-parsers/view-objects/plot/content/plot-content-parser";
import { ColoredPlotContentParser } from "../core/settings-parsers/view-objects/plot/content/colored-plot-content-parser";
import { MeanderPlotContentParser } from "../core/settings-parsers/view-objects/plot/content/meander-plot-content-parser";
import { SignalsPlotContentParser } from "../core/settings-parsers/view-objects/plot/content/signals-plot-content-parser";
import { MeasurerPlotContentParser } from "../core/settings-parsers/view-objects/plot/content/measurer-plot-content-parser";
import { CursorPlotContentParser } from "../core/settings-parsers/view-objects/plot/content/cursor-plot-content-parser";
import { SignalLabelPlotContentParser } from "../core/settings-parsers/view-objects/plot/content/signal-label-plot-content-parser";
import { MeanValueLabelPlotContentParser } from "../core/settings-parsers/view-objects/plot/content/mean-value-label-plot-content-parser";
import { PlotControlsContentParser } from "../core/settings-parsers/view-objects/plot/content/plot-controls-content-parser";

import { PlotSaveableSettingsProvider } from "../core/saveable-settings/view-objects/plot-saveable-settings-provider";
import { GridSaveableSettingsProvider } from "../core/saveable-settings/view-objects/grid-saveable-settings-provider";
import { ColumnGridSaveableSettingsProvider } from "../core/saveable-settings/view-objects/column-grid-saveable-settings-provider";
import { PanelSaveableSettingsProvider } from "../core/saveable-settings/view-objects/panel-saveable-settings-provider";
import { TableSaveableSettingsProvider } from "../core/saveable-settings/view-objects/table-saveable-settings-provider";
import { CanvasSaveableSettingsProvider } from "../core/saveable-settings/view-objects/canvas-saveable-settings-provider";
import { ViewSaveableSettingsProvider } from "../core/saveable-settings/view-saveable-settings-provider";

import { IdGenerator } from "../../../../web-visualizator";
import { SignalsCreator } from "../core/view-object-creators/plot/signals-creator";
import { VisualizatorCreator } from "../core/view-object-creators/visualizator-creator";
import { FpsCanvasCreator } from "../core/view-object-creators/fps-canvas-creator";
import { GridCreator } from "../core/view-object-creators/grid-creator";
import { ColumnGridCreator } from "../core/view-object-creators/column-grid-creator";
import { PanelCreator } from "../core/view-object-creators/panel-creator";
import { TableCreator } from "../core/view-object-creators/table-creator";
import { OfflinePlotCreator } from "./offline-plot-creator";

import { VisualizatorWebglSettingsConverter } from "../core/webgl-settings/visualizator-webgl-settings-converter";
import { ViewWebglSettingsConverter } from "../core/webgl-settings/view-webgl-settings-converter";
import { CanvasWebglSettingsConverter } from "../core/webgl-settings/canvas-webgl-settings-converter";
import { GridWebglSettingsConverter } from "../core/webgl-settings/view-objects/grid-webgl-settings-converter";
import { ColumnGridWebglSettingsConverter } from "../core/webgl-settings/view-objects/column-grid-webgl-settings-converter";
import { TableWebglSettingsConverter } from "../core/webgl-settings/view-objects/table-webgl-settings-converter";
import { PanelWebglSettingsConverter } from "../core/webgl-settings/view-objects/panel-webgl-settings-converter";
import { OfflinePlotWebglSettingsConverter } from "./offline-plot-webgl-settings-converter";

import { DataSourceWebglSettingsConverter } from "../core/webgl-settings/view-objects/plot/data-source-webgl-settings-converter";
import { RhythmChannelWebglSettingsConverter } from "../core/webgl-settings/view-objects/plot/rhythm-channel-webgl-settings-converter";
import { ChannelWebglSettingsConverter } from "../core/webgl-settings/view-objects/plot/channel-webgl-settings-converter";
import { ChannelConfigurationWebglSettingsConverter } from "../core/webgl-settings/view-objects/plot/channel-configuration-webgl-settings-converter";
import { SignalWebglSettingsConverter } from "../core/webgl-settings/view-objects/plot/signal-webgl-settings-converter";

import offlineVisualizatorDefaultSettings from "./offline-visualizator-default-settings.json";

export class OfflineVisualizatorParser extends VisualizatorParser {
  constructor() {
    super();

    let modeSettingsParser = new ModeSettingsParser(new SimpleSettingsParser());

    let filterParser = new FilterParser();
    let commonSignalParser = new CommonSignalParser();
    let commonConfigurationParser = new CommonChannelConfigurationParser(commonSignalParser);
    let commonChannelParser = new CommonChannelParser(commonConfigurationParser);
    let commonMarkParser = new CommonMarkParser();
    let commonDataSourceParser = new CommonDataSourceParser(commonChannelParser, commonMarkParser);
    const unitParser = new UnitSettingsParser();
    let dataParser = new FilteredDataSettingsParser(filterParser, commonDataSourceParser, unitParser);

    let viewObjectParsers = new Map();
    viewObjectParsers.set("panel", new PanelSettingsParser(viewObjectParsers));
    viewObjectParsers.set("grid", new GridSettingsParser(viewObjectParsers));
    viewObjectParsers.set("column-grid", new ColumnGridSettingsParser(viewObjectParsers));
    viewObjectParsers.set("table", new TableSettingsParser(viewObjectParsers));

    let channelConfigPlotContentParsers = new Map();
    channelConfigPlotContentParsers.set("signalLabels", new PlotContentParser(new SettingsArrayParser(new SignalLabelPlotContentParser())));
    channelConfigPlotContentParsers.set(
      "meanValueLabels",
      new PlotContentParser(new SettingsArrayParser(new MeanValueLabelPlotContentParser()))
    );
    channelConfigPlotContentParsers.set("isolineLabels", new PlotContentParser(new SettingsArrayParser(new SimpleSettingsParser())));
    channelConfigPlotContentParsers.set("_default", new PlotContentParser(new SimpleSettingsParser()));

    let plotSignalParser = new SignalPlotSettingsParser();
    let plotIsolineParser = new IsolinePlotSettingsParser();
    let plotChannelConfigurationParser = new ChannelConfigurationPlotSettingsParser(
      plotSignalParser,
      plotIsolineParser,
      channelConfigPlotContentParsers
    );
    let plotChannelParser = new ChannelPlotSettingsParser(plotChannelConfigurationParser);
    let plotMarkParser = new MarkPlotSettingsParser();
    let plotDataSourceParser = new DataSourcePlotSettingsParser(plotChannelParser, plotMarkParser);

    let plotContentParsers = new Map();
    plotContentParsers.set("plotControls", new PlotContentParser(new PlotControlsContentParser()));
    plotContentParsers.set("grid", new PlotContentParser(new ColoredPlotContentParser()));
    plotContentParsers.set("border", new PlotContentParser(new ColoredPlotContentParser()));
    plotContentParsers.set("cursor", new PlotContentParser(new CursorPlotContentParser()));
    plotContentParsers.set("marks", new PlotContentParser(new ColoredPlotContentParser()));
    plotContentParsers.set("frameCounterErrors", new PlotContentParser(new ColoredPlotContentParser()));
    plotContentParsers.set("meander", new PlotContentParser(new MeanderPlotContentParser()));
    plotContentParsers.set("measurer", new PlotContentParser(new MeasurerPlotContentParser()));
    plotContentParsers.set("signals", new PlotContentParser(new SignalsPlotContentParser()));

    viewObjectParsers.set("plot:offline:base", new PlotSettingsParser(plotDataSourceParser, plotContentParsers));
    viewObjectParsers.set("plot:offline:rhythm", new PlotSettingsParser(plotDataSourceParser, plotContentParsers));

    let canvasParser = new FpsCanvasParser(viewObjectParsers);
    let viewParser = new ViewSettingsParser(canvasParser);

    this.settingsParser = new VisualizatorSettingsParser(modeSettingsParser, dataParser, viewParser);

    //====================== Create Saveable Providers =====================
    const viewObjectSaveableSettingsProviders = new Map();
    viewObjectSaveableSettingsProviders.set("panel", new PanelSaveableSettingsProvider(viewObjectSaveableSettingsProviders));
    viewObjectSaveableSettingsProviders.set("grid", new GridSaveableSettingsProvider(viewObjectSaveableSettingsProviders));
    viewObjectSaveableSettingsProviders.set("column-grid", new ColumnGridSaveableSettingsProvider(viewObjectSaveableSettingsProviders));
    viewObjectSaveableSettingsProviders.set("table", new TableSaveableSettingsProvider(viewObjectSaveableSettingsProviders));
    viewObjectSaveableSettingsProviders.set("plot:offline:base", new PlotSaveableSettingsProvider());
    viewObjectSaveableSettingsProviders.set("plot:offline:rhythm", new PlotSaveableSettingsProvider());

    const canvasSaveableSettingsProvider = new CanvasSaveableSettingsProvider(viewObjectSaveableSettingsProviders);
    this.saveableSettingsProvider = new ViewSaveableSettingsProvider(canvasSaveableSettingsProvider);
  }

  parseSettings(settings) {
    this.commonSettings = this.settingsParser.parse(settings, offlineVisualizatorDefaultSettings);
    return this.commonSettings;
  }

  createSaveableSettings(settings = this.commonSettings) {
    return this.saveableSettingsProvider.createSaveableSettings(settings);
  }

  applySaveableSettings(settings, saveableSettings) {
    this.saveableSettingsProvider.applySaveableSettings(settings, saveableSettings);
  }

  makeWebglConverters() {
    let viewObjectConverters = new Map();
    viewObjectConverters.set("panel", new PanelWebglSettingsConverter(viewObjectConverters));
    viewObjectConverters.set("grid", new GridWebglSettingsConverter(viewObjectConverters));
    viewObjectConverters.set("column-grid", new ColumnGridWebglSettingsConverter(viewObjectConverters));
    viewObjectConverters.set("table", new TableWebglSettingsConverter(viewObjectConverters));

    let signalConverter = new SignalWebglSettingsConverter(this.commonSettings);
    let channelConfigConverter = new ChannelConfigurationWebglSettingsConverter(this.commonSettings, signalConverter);

    let channelConverter = new ChannelWebglSettingsConverter(this.commonSettings, channelConfigConverter);
    let dataSourceConverter = new DataSourceWebglSettingsConverter(this.commonSettings, channelConverter);
    viewObjectConverters.set("plot:offline:base", new OfflinePlotWebglSettingsConverter(dataSourceConverter, new SignalsCreator()));

    let rhythmChannelConverter = new RhythmChannelWebglSettingsConverter(this.commonSettings, channelConfigConverter);
    let rhythmDataSourceConverter = new DataSourceWebglSettingsConverter(this.commonSettings, rhythmChannelConverter);
    viewObjectConverters.set("plot:offline:rhythm", new OfflinePlotWebglSettingsConverter(rhythmDataSourceConverter, new SignalsCreator()));

    let canvasWebglSettingsConverter = new CanvasWebglSettingsConverter(viewObjectConverters);
    let viewWebglSettingsConverter = new ViewWebglSettingsConverter(canvasWebglSettingsConverter);
    this.webglSettingsConverter = new VisualizatorWebglSettingsConverter(viewWebglSettingsConverter);
  }

  createWebglSettings() {
    this.webglCommonSettings = this.webglSettingsConverter.convert(this.commonSettings);
    return this.webglCommonSettings;
  }

  makeObjectCreators() {
    this.idGenerator = new IdGenerator();
    this.viewObjectCreators = new Map();
    this.viewObjectCreators.set("panel", new PanelCreator(this.webglCommonSettings));
    this.viewObjectCreators.set("grid", new GridCreator(this.webglCommonSettings));
    this.viewObjectCreators.set("column-grid", new ColumnGridCreator(this.webglCommonSettings));
    this.viewObjectCreators.set("table", new TableCreator(this.webglCommonSettings));
    this.viewObjectCreators.set("plot:offline:base", new OfflinePlotCreator(this.webglCommonSettings));
    this.viewObjectCreators.set("plot:offline:rhythm", new OfflinePlotCreator(this.webglCommonSettings));

    let canvasCreator = new FpsCanvasCreator(this.webglCommonSettings, this.viewObjectCreators, this.idGenerator);
    this.visualizatorCreator = new VisualizatorCreator(canvasCreator, this.webglCommonSettings);
  }

  createObjects() {
    return this.visualizatorCreator.create(this.webglCommonSettings.view);
  }
}
